import { useTranslation } from 'react-i18next';
import { formatGetal, isTelling } from '../format';
import { PADREGEL } from '../veldnaam';

export interface FixedFactsProps {
  /** De vaste factor tussen twee beoordelaars, zoals het signaleringsantwoord hem meldt. */
  signaleringsfactor?: number;
  /** Het aantal beoordeelde producten dat elk van de twee minstens moet hebben. */
  minimumBeoordeeldeProducten?: number;
  /**
   * De uitgesloten velden zoals het antwoord ze noemt, of `null` wanneer dit antwoord ze niet noemt.
   *
   * Drie gevallen en geen twee, net als in de verantwoordingsvoet: een lijst met namen, een lege
   * lijst ("er is niets uitgesloten") en geen lijst. Die laatste twee op één hoop gooien maakt van
   * "wij weten het hier niet" de bewering "er zijn er geen".
   */
  uitgeslotenVelden: readonly string[] | null;
}

/**
 * Wat vastligt en niet te verzetten is (story 4.5, UX-DR14, AD-5, FR8; story 12, S7 Vaste-regels-kaart).
 *
 * **Leesbare feiten, geen invoervelden.** Alleen drempel en doel zijn instelbaar; alles wat hier staat
 * ligt vast. Een invoerveld hier zou beweren dat het anders kan.
 *
 * **Het portaal kent de vier velden niet uit zichzelf** (AD-5): het toont wat de API teruggeeft.
 * Een tweede lijst in deze code zou uit de pas gaan lopen met de constante die de meting voedt, en
 * dat is precies de fout die `lastChangeDateTime` en `effectiveDateTime` bovenaan de eerste meting
 * zette. Er staat daarom geen telling bij: die velden worden niet gemeten, dus er valt niets te
 * tellen — ook geen nul. Alleen de Nederlandse naam per veld staat in nl.json en en.json (A8), op het
 * laatste padsegment; een pad zonder naam staat er alleen als pad.
 */
export function FixedFacts({
  signaleringsfactor,
  minimumBeoordeeldeProducten,
  uitgeslotenVelden,
}: FixedFactsProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;

  return (
    <div
      className="flex flex-col gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--background-muted)] p-5"
      data-testid="werkkwaliteit-vastligt"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[13px] font-semibold text-[var(--foreground-heading)]">
          {t('qa.instellingen.vastligtKop')}
        </h2>
        <span className="rounded-full border border-[var(--border)] bg-[var(--background)] px-2 py-0.5 text-[12px] text-[var(--foreground-muted)]">
          {t('qa.instellingen.vastligtMarkering')}
        </span>
      </div>

      {/* De signaleringsgrens volgt de instelbare drempel niet, en dat hoort er letterlijk te staan
          (M11). De getallen komen uit de afbakening van `/signals`. */}
      <h3 className="text-[13px] font-semibold text-[var(--foreground-heading)]">
        {t('qa.instellingen.signaleringKop')}
      </h3>
      <p className="text-[13px] text-[var(--foreground)]" data-testid="werkkwaliteit-signaleringsgrens">
        {isTelling(signaleringsfactor) && isTelling(minimumBeoordeeldeProducten)
          ? t('qa.instellingen.signaleringsgrens', {
              factor: formatGetal(signaleringsfactor, locale),
              minimum: formatGetal(minimumBeoordeeldeProducten, locale),
            })
          : t('qa.instellingen.signaleringsgrensOnbekend')}
      </p>

      <h3 className="text-[13px] font-semibold text-[var(--foreground-heading)]">
        {t('qa.instellingen.uitgeslotenKop')}
      </h3>
      <p className="text-[13px] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-uitgesloten-uitleg">
        {t('qa.instellingen.uitgeslotenUitleg')}
      </p>
      {uitgeslotenVelden === null ? (
        <p className="text-[13px] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-uitgesloten-onbekend">
          {t('qa.instellingen.uitgeslotenOnbekend')}
        </p>
      ) : uitgeslotenVelden.length === 0 ? (
        <p className="text-[13px] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-uitgesloten-geen">
          {t('qa.instellingen.uitgeslotenGeen')}
        </p>
      ) : (
        <ul
          className="flex flex-col gap-2 text-[13px] text-[var(--foreground)]"
          data-testid="werkkwaliteit-uitgesloten-velden"
        >
          {uitgeslotenVelden.map((veld, plek) => {
            // De naam komt uit nl.json en en.json (A8), op het laatste padsegment; zonder naam alleen het pad.
            const segment = String(veld).split('/').filter(Boolean).at(-1) ?? '';
            const naam =
              // Een vertaalsleutel en geen zichtbare tekst: de tekst zelf komt uit t() hieronder.
              // eslint-disable-next-line i18next/no-literal-string
              segment !== '' && i18n.exists(`qa.instellingen.uitgeslotenNaam.${segment}`)
                ? t(`qa.instellingen.uitgeslotenNaam.${segment}`)
                : null;
            return (
              <li className="min-w-0" key={`${veld}-${plek}`}>
                {naam !== null && <span className="block">{naam}</span>}
                <span className={naam !== null ? PADREGEL : 'block break-all font-mono text-[12px]'} lang="en">
                  {veld}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
